import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AlertTriangle, RefreshCw, Webhook, ChevronDown, ChevronRight } from "lucide-react";

type WebhookEvent = {
  id: string;
  environment: string;
  event_id: string | null;
  event_type: string | null;
  status: "processed" | "skipped" | "failed" | "invalid_signature" | "invalid_env";
  error_message: string | null;
  signature_valid: boolean | null;
  processing_ms: number | null;
  payload: any;
  created_at: string;
};

const STATUS_STYLES: Record<string, string> = {
  processed: "border-emerald-500/40 text-emerald-500",
  skipped: "border-muted-foreground/40 text-muted-foreground",
  failed: "border-destructive/60 text-destructive bg-destructive/5",
  invalid_signature: "border-destructive/60 text-destructive bg-destructive/5",
  invalid_env: "border-amber-500/50 text-amber-500",
};

export function WebhookEventsPanel() {
  const [events, setEvents] = useState<WebhookEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [envFilter, setEnvFilter] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("stripe_webhook_events")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(200);
    setEvents((data as WebhookEvent[]) ?? []);
    setLoading(false);
  };

  useEffect(() => {
    load();
    const ch = supabase
      .channel(`stripe_webhook_events_admin:${Math.random().toString(36).slice(2)}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "stripe_webhook_events" },
        (p) => setEvents((prev) => [p.new as WebhookEvent, ...prev].slice(0, 200)),
      )
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
  }, []);

  const filtered = useMemo(() => {
    return events.filter((e) => {
      if (statusFilter !== "all" && e.status !== statusFilter) return false;
      if (envFilter !== "all" && e.environment !== envFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        const hay = `${e.event_type ?? ""} ${e.event_id ?? ""} ${e.error_message ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [events, statusFilter, envFilter, search]);

  const stats = useMemo(() => {
    const last24 = Date.now() - 24 * 60 * 60 * 1000;
    const recent = events.filter((e) => new Date(e.created_at).getTime() > last24);
    return {
      total24h: recent.length,
      failed24h: recent.filter((e) => e.status === "failed" || e.status === "invalid_signature").length,
      skipped24h: recent.filter((e) => e.status === "skipped").length,
      processed24h: recent.filter((e) => e.status === "processed").length,
    };
  }, [events]);

  const alerting = stats.failed24h > 0;

  return (
    <div className="space-y-6">
      {alerting && (
        <div className="rounded-xl border border-destructive/60 bg-destructive/10 p-4 flex items-start gap-3">
          <AlertTriangle className="size-4 text-destructive mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="font-medium text-sm">
              {stats.failed24h} failed or invalid Stripe webhook
              {stats.failed24h === 1 ? "" : "s"} in the last 24h
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Inspect failed rows below — Stripe will retry for up to 3 days. Check signature
              secrets and handler errors first.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Total (24h)" value={stats.total24h} />
        <Stat label="Processed" value={stats.processed24h} tone="ok" />
        <Stat label="Skipped" value={stats.skipped24h} />
        <Stat label="Failed" value={stats.failed24h} tone={stats.failed24h > 0 ? "bad" : "default"} />
      </div>

      <div className="rounded-xl border border-border bg-card p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Webhook className="size-4 text-primary" />
            <h2 className="font-display text-lg font-bold">Stripe webhook events</h2>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="type, event id, error…"
              className="h-8 w-56 font-mono text-xs"
            />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-8 w-36 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="processed">Processed</SelectItem>
                <SelectItem value="skipped">Skipped</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="invalid_signature">Invalid signature</SelectItem>
                <SelectItem value="invalid_env">Invalid env</SelectItem>
              </SelectContent>
            </Select>
            <Select value={envFilter} onValueChange={setEnvFilter}>
              <SelectTrigger className="h-8 w-32 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All envs</SelectItem>
                <SelectItem value="sandbox">Sandbox</SelectItem>
                <SelectItem value="live">Live</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={load} disabled={loading}>
              <RefreshCw className={`size-3.5 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>

        {filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {loading ? "Loading…" : "No webhook events match the current filters."}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left font-mono text-[10px] uppercase text-muted-foreground border-b border-border">
                  <th className="py-2 w-6" />
                  <th>When</th>
                  <th>Env</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Latency</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((e) => {
                  const isOpen = expanded === e.id;
                  return (
                    <>
                      <tr
                        key={e.id}
                        className="border-b border-border/60 cursor-pointer hover:bg-muted/40"
                        onClick={() => setExpanded(isOpen ? null : e.id)}
                      >
                        <td className="py-2">
                          {isOpen ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
                        </td>
                        <td className="font-mono text-[10px] text-muted-foreground whitespace-nowrap">
                          {new Date(e.created_at).toLocaleString()}
                        </td>
                        <td className="font-mono text-[10px]">{e.environment}</td>
                        <td className="font-mono text-xs">{e.event_type ?? "—"}</td>
                        <td>
                          <span
                            className={`font-mono text-[10px] uppercase rounded px-1.5 py-0.5 border ${
                              STATUS_STYLES[e.status] ?? ""
                            }`}
                          >
                            {e.status.replace("_", " ")}
                          </span>
                        </td>
                        <td className="font-mono text-[10px] text-muted-foreground">
                          {e.processing_ms != null ? `${e.processing_ms}ms` : "—"}
                        </td>
                        <td className="text-xs text-destructive max-w-[280px] truncate">
                          {e.error_message ?? ""}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr key={`${e.id}-d`} className="border-b border-border/60 bg-muted/20">
                          <td />
                          <td colSpan={6} className="py-3 pr-3">
                            <div className="space-y-2">
                              <div className="font-mono text-[10px] text-muted-foreground">
                                event_id: {e.event_id ?? "—"} · signature_valid: {String(e.signature_valid)}
                              </div>
                              {e.error_message && (
                                <pre className="text-xs bg-background/60 rounded p-2 overflow-x-auto whitespace-pre-wrap break-words text-destructive">
                                  {e.error_message}
                                </pre>
                              )}
                              <details>
                                <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
                                  Show payload
                                </summary>
                                <pre className="mt-2 text-[10px] bg-background/60 rounded p-2 overflow-x-auto max-h-80">
                                  {JSON.stringify(e.payload, null, 2)}
                                </pre>
                              </details>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, tone = "default" }: { label: string; value: number; tone?: "default" | "ok" | "bad" }) {
  const toneClass =
    tone === "bad"
      ? "text-destructive"
      : tone === "ok"
      ? "text-emerald-500"
      : "text-foreground";
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="font-mono text-[10px] uppercase text-muted-foreground">{label}</div>
      <div className={`font-display text-2xl font-bold mt-1 ${toneClass}`}>{value}</div>
    </div>
  );
}
